/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import { Field, FastField, FieldArray, useField } from 'formik'
import {
    Label,
    FormFeedback,
    Input,
    InputProps,
    FormGroup,
    FormGroupProps
} from '@bootstrap-styled/v4'

// app components
import AddField from './AddField'
import Select from './Select'
import InputMask from './InputMask'
import Autocomplete from './Autocomplete'

// type definition
type PassInputProps = Omit<InputProps, 'children' | 'ref' | 'name' | 'type' | 'value' | 'tag'> &
    Pick<FormGroupProps, 'row' | 'check' | 'inline' | 'tag'>

type FieldComponents =
    | typeof Field
    | typeof FastField
    | typeof FieldArray
    | typeof AddField
    | typeof Select
    | typeof InputMask
    | typeof Autocomplete

type AssertFieldProps<T> = T extends FieldComponents ? React.ComponentProps<T> : { children?: any } // TODO: Usar: React.ComponentProps<typeof Field> por defecto en vez de { children?: any }

type LabelOptsProps = {
    htmlFor: string
    isRequired?: boolean
}
type LabelRenderProp = string | ((opts: LabelOptsProps) => React.ReactNode)

export interface TextFieldProps extends PassInputProps, AssertFieldProps<FieldComponents> {
    name: string
    label: LabelRenderProp
    type?: InputProps['type'] | 'autocomplete' | 'inputmask'
    fieldComp?: FieldComponents
    required?: boolean
    disabled?: boolean
    hiddenLabel?: boolean
    error?: FormGroupProps['color'] | boolean
}

/**
 * This component manages, either all form input field with a label
 * @param {Object} Props to configure the text input field
 * @return {React.ReactElement}
 */
const TextField: React.FC<TextFieldProps> = ({
    id,
    label,
    name,
    type,
    error,
    required,
    disabled,
    children,
    row,
    check,
    inline,
    tag,
    hiddenLabel,
    fieldComp = Field,
    ...props
}) => {
    const meta = useField({ name, type })[1]
    const validated = typeof error === 'undefined' ? !!(meta.error && meta.touched) : error
    const validationState = !!validated
        ? typeof validated === 'string'
            ? validated
            : 'danger'
        : ''

    // Swaps component between field type, input type or both
    let passProps = {}
    let FieldT = fieldComp
    let InputComponent: typeof Autocomplete | typeof Input = Input
    switch (type && type.toLowerCase()) {
        case 'select':
            FieldT = Select
            passProps = { ...props }
            break
        case 'autocomplete':
            InputComponent = Autocomplete
            passProps = { ...props, as: InputComponent }
            break
        case 'inputmask':
            FieldT = InputMask
            passProps = { ...props }
            break
        default:
            InputComponent = Input
            passProps = { ...props, type: type, as: InputComponent }
    }

    return (
        <FormGroup
            color={validationState}
            row={row}
            check={check}
            inline={inline}
            tag={tag}
            disabled={disabled}
        >
            {typeof label === 'function' && 'call' in label ? (
                label({ htmlFor: id || name, isRequired: required })
            ) : (
                <Label
                    for={id || name}
                    className={required ? 'form-control-required' : ''}
                    hidden={hiddenLabel}
                >
                    {label}
                </Label>
            )}
            <FieldT name={name} id={id} disabled={disabled} {...passProps}>
                {children}
            </FieldT>
            {typeof meta.error === 'string' && validated ? (
                <FormFeedback>{meta.error}</FormFeedback>
            ) : null}
        </FormGroup>
    )
}

export default TextField
